
import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { HelpCircle, Search, BookOpen, ExternalLink } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';

interface HelpTopic {
  id: string;
  title: string;
  content: React.ReactNode;
  keywords: string[];
  category: 'general' | 'compliance' | 'export' | 'reporting';
}

interface ContextualHelpProps {
  contextId?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
}

export function ContextualHelp({
  contextId,
  position = 'right',
  size = 'md'
}: ContextualHelpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('context');
  
  // Define help topics
  const helpTopics: HelpTopic[] = [
    {
      id: 'document-management',
      title: 'Document Management',
      category: 'general',
      keywords: ['document', 'upload', 'manage', 'review'],
      content: (
        <div className="space-y-2">
          <p>
            The document management system allows you to upload, organize, and review 
            various documents related to compliance and reporting.
          </p>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li>Upload documents for review and approval</li>
            <li>Track document status through the approval workflow</li>
            <li>Comment on documents for collaborative review</li>
            <li>Export documents for reporting purposes</li>
          </ul>
        </div>
      )
    },
    {
      id: 'certificate-compliance',
      title: 'Certificate Compliance',
      category: 'compliance',
      keywords: ['certificate', 'compliance', 'regulation', 'reporting'],
      content: (
        <div className="space-y-2">
          <p>
            Certificate compliance ensures all issued certificates meet regulatory 
            requirements and are properly reported to authorities.
          </p>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li>Track certificate reporting status</li>
            <li>Verify certificates against regulatory requirements</li>
            <li>Generate compliance reports for regulatory submissions</li>
            <li>Maintain audit trail of certificate verification</li>
          </ul>
        </div>
      )
    },
    {
      id: 'export-formats',
      title: 'Export Formats',
      category: 'export',
      keywords: ['export', 'format', 'csv', 'excel', 'pdf', 'regulatory'],
      content: (
        <div className="space-y-2">
          <p>
            Different export formats are available depending on your needs:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li><strong>CSV</strong> - Simple format compatible with most spreadsheet applications</li>
            <li><strong>Excel (XLSX)</strong> - Rich formatting with multiple worksheets</li>
            <li><strong>PDF</strong> - Professional document format for formal reports</li>
            <li><strong>Regulatory</strong> - State-specific formats for official submissions</li>
          </ul>
        </div>
      )
    },
    {
      id: 'state-reporting',
      title: 'State Reporting Requirements',
      category: 'reporting',
      keywords: ['state', 'reporting', 'compliance', 'requirements', 'regulation'],
      content: (
        <div className="space-y-2">
          <p>
            State reporting requirements vary by jurisdiction and may include:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li><strong>Florida</strong> - Quarterly reporting with specific formatting</li>
            <li><strong>Texas</strong> - Monthly summaries with student verification</li>
            <li><strong>California</strong> - Bi-annual reports with instructor verification</li>
            <li><strong>New York</strong> - Annual compliance reporting with audit trail</li>
          </ul>
          <p className="text-xs mt-2">
            Check the state-specific guidelines for detailed requirements.
          </p>
        </div>
      )
    },
    {
      id: 'inspection-reports',
      title: 'Inspection Reports',
      category: 'compliance',
      keywords: ['inspection', 'report', 'audit', 'compliance', 'regulatory'],
      content: (
        <div className="space-y-2">
          <p>
            Inspection reports are formatted documents that comply with regulatory 
            requirements for official reviews and audits.
          </p>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li>Generate reports for specific time periods</li>
            <li>Include verification and reporting status</li>
            <li>Format according to state-specific requirements</li>
            <li>Track report submission history</li>
          </ul>
        </div>
      )
    }
  ];
  
  // Filter topics based on context and search
  const filteredTopics = helpTopics.filter(topic => {
    const matchesSearch = searchQuery 
      ? topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.keywords.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
      : true;
      
    const matchesContext = contextId
      ? topic.id === contextId || 
        (contextId === 'export' && topic.category === 'export') ||
        (contextId === 'compliance' && topic.category === 'compliance') ||
        (contextId === 'reporting' && topic.category === 'reporting')
      : true;
      
    return matchesSearch && (activeTab === 'search' ? true : matchesContext);
  });
  
  const sizeClasses = {
    sm: 'w-72',
    md: 'w-96',
    lg: 'w-[500px]'
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label="Get help"
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`${sizeClasses[size]} p-0`}
        align={position === 'left' ? 'start' : position === 'right' ? 'end' : 'center'}
        side={position === 'top' ? 'top' : position === 'bottom' ? 'bottom' : 'right'}
      >
        <Tabs 
          defaultValue="context" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <div className="flex items-center justify-between p-4 pb-2">
            <TabsList>
              <TabsTrigger value="context">Contextual</TabsTrigger>
              <TabsTrigger value="search">Search</TabsTrigger>
            </TabsList>
            <Button variant="ghost" size="icon" asChild>
              <a href="/admin/help" target="_blank" rel="noopener">
                <BookOpen className="h-4 w-4" />
              </a>
            </Button>
          </div>
          
          <TabsContent value="search" className="mt-0 p-4 pt-0">
            <div className="relative mb-4">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for help topics..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </TabsContent>
          
          <div className="px-4 pb-4 pt-2 space-y-4 max-h-[300px] overflow-auto">
            {filteredTopics.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <p>No help topics found for "{searchQuery}"</p>
                <p className="text-sm mt-1">Try different keywords or browse all topics</p>
              </div>
            ) : (
              filteredTopics.map(topic => (
                <div key={topic.id} className="space-y-2">
                  <h4 className="font-medium text-sm">{topic.title}</h4>
                  <div className="text-xs text-muted-foreground">
                    {topic.content}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="border-t p-3 bg-muted/40 flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              Can't find what you need?
            </span>
            <a 
              href="/admin/documentation" 
              className="text-xs flex items-center text-primary"
              target="_blank"
              rel="noopener"
            >
              View full documentation
              <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
